import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    addDocumentCustomFieldProperty,
    createDocumentCustomField,
} from "@/api/documents";
import { getApiErrorMessage, IApiAxiosError } from "@/api/api.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { clampPageInput } from "@/utils/fields";
import { PlusIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

// Two modes:
//   1. customFieldId is set     → add property to that custom field directly.
//   2. customFieldId is undefined → bridge mode: create a custom field tied to
//      (policyTypeId, fieldDefinitionId, fieldName) and seed the new property
//      in one request.
export interface AddPropertyInlineFormProps {
    documentId: number;
    /** Existing custom field to attach the property to. Mutually exclusive with bridge props. */
    customFieldId?: number;
    /** Bridge mode: create-or-extend a custom field tied to this scalar field. */
    policyTypeId?: number;
    fieldDefinitionId?: number;
    fieldName?: string;
    /** Current visible PDF page (1-indexed), used as default page number */
    currentPage?: number;
    /** Total pages in the PDF, used for validation max */
    totalPages?: number;
}

interface FormState {
    name: string;
    value: string;
    page: string;
}

export function AddPropertyInlineForm({
    documentId,
    customFieldId,
    policyTypeId,
    fieldDefinitionId,
    fieldName,
    currentPage,
    totalPages,
}: AddPropertyInlineFormProps) {
    const queryClient = useQueryClient();

    const [isOpen, setIsOpen] = useState(false);
    const [form, setForm] = useState<FormState>({
        name: "",
        value: "",
        page: currentPage ? String(currentPage) : "",
    });
    const [nameError, setNameError] = useState(false);
    const [valueError, setValueError] = useState(false);

    const nameRef = useRef<HTMLInputElement>(null);
    const valueRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const { mutate, isPending } = useMutation({
        mutationFn: (data: {
            name: string;
            value: string | null;
            page_number: number | null;
        }) => {
            if (customFieldId !== undefined) {
                return addDocumentCustomFieldProperty(
                    documentId,
                    customFieldId,
                    {
                        name: data.name,
                        value: data.value,
                        page_number: data.page_number,
                    }
                );
            }
            // Bridge mode: create a custom field tied to the extracted scalar
            // field's definition and seed the first property.
            if (
                policyTypeId === undefined ||
                fieldDefinitionId === undefined ||
                !fieldName
            ) {
                throw new Error(
                    "AddPropertyInlineForm requires either customFieldId or full bridge props"
                );
            }
            return createDocumentCustomField(documentId, {
                name: fieldName,
                policy_type_id: policyTypeId,
                field_definition_id: fieldDefinitionId,
                properties: [
                    {
                        name: data.name,
                        value: data.value,
                        page_number: data.page_number,
                    },
                ],
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
            reset();
        },
        onError: (error: IApiAxiosError) => {
            toast.error(getApiErrorMessage(error, "Failed to add property"));
        },
    });

    function reset() {
        setForm({
            name: "",
            value: "",
            page: currentPage ? String(currentPage) : "",
        });
        setNameError(false);
        setValueError(false);
        setIsOpen(false);
    }

    // Native (not synthetic) keydown listener so Esc cancels the form WITHOUT
    // bubbling up to the panel's `usePanelEscape` listener (which is also
    // native and would otherwise close the whole panel). React synthetic
    // events fire after the bubble has already visited every ancestor — too
    // late to spare the panel.
    useEffect(() => {
        if (!isOpen) return;
        const node = formRef.current;
        if (!node) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            reset();
        };
        node.addEventListener("keydown", handler);
        return () => node.removeEventListener("keydown", handler);
        // `reset` reads from refs/state but is stable enough for this guard;
        // omitting it keeps the listener attach/detach tied to `isOpen`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!form.name.trim()) {
            setNameError(true);
            nameRef.current?.focus();
            return;
        }
        if (!form.value.trim()) {
            setValueError(true);
            valueRef.current?.focus();
            return;
        }

        const pageNum = form.page.trim() ? parseInt(form.page, 10) : null;
        if (pageNum !== null && (isNaN(pageNum) || pageNum < 1)) return;
        if (pageNum !== null && totalPages && pageNum > totalPages) return;

        mutate({
            name: form.name.trim(),
            value: form.value.trim(),
            page_number: pageNum,
        });
    }

    if (!isOpen) {
        return (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="text-muted-foreground hover:bg-muted/40 hover:text-foreground flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs transition-colors"
            >
                <PlusIcon size={12} />
                Add property
            </button>
        );
    }

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex items-center gap-1.5 px-1 py-1"
        >
            <Input
                ref={nameRef}
                placeholder="Property name *"
                value={form.name}
                onChange={(e) => {
                    setForm((p) => ({ ...p, name: e.target.value }));
                    if (nameError) setNameError(false);
                }}
                disabled={isPending}
                aria-invalid={nameError}
                className={`h-7 min-w-0 flex-[2] text-xs ${nameError ? "border-destructive" : ""}`}
                autoFocus
                autoComplete="off"
            />
            <Input
                ref={valueRef}
                placeholder="Value *"
                value={form.value}
                onChange={(e) => {
                    setForm((p) => ({ ...p, value: e.target.value }));
                    if (valueError) setValueError(false);
                }}
                disabled={isPending}
                aria-invalid={valueError}
                className={`h-7 min-w-0 flex-[2] text-xs ${valueError ? "border-destructive" : ""}`}
                autoComplete="off"
            />
            <Input
                type="number"
                placeholder={currentPage ? String(currentPage) : "Pg"}
                min={1}
                max={totalPages}
                value={form.page}
                onChange={(e) => {
                    const clamped = clampPageInput(e.target.value, totalPages);
                    if (clamped !== null)
                        setForm((p) => ({ ...p, page: clamped }));
                }}
                disabled={isPending}
                className="h-7 w-14 shrink-0 [appearance:textfield] text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoComplete="off"
                aria-label="Page number"
            />
            <Button
                type="submit"
                variant="default"
                size="icon"
                disabled={isPending}
                className="size-7 shrink-0"
                aria-label="Add property"
            >
                {isPending ? (
                    <Spinner className="h-3 w-3" />
                ) : (
                    <PlusIcon size={14} />
                )}
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={reset}
                disabled={isPending}
                className="size-7 shrink-0"
                aria-label="Cancel"
            >
                <XIcon size={14} />
            </Button>
        </form>
    );
}
